/** Fixed header band height in PDF points (1/72 inch). */
export const PDF_HEADER_HEIGHT_PT = 72;

/** PDF Info Keywords token written when a Sourcery header has been applied. */
export const SOURCERY_HEADER_KEYWORD = 'sourcery-header-v1';

const HEADER_HORIZONTAL_MARGIN_PT = 36;
const HEADER_TOP_MARGIN_PT = 8;
const HEADER_CELL_GAP_PT = 12;
/** Right column width for product type / designation (pt). */
const HEADER_COL3_WIDTH_PT = 148;
const HEADER_COL3_MIN_WIDTH_PT = 96;
const HEADER_ELLIPSIS = '…';
const HEADER_FONT_SIZE = 9;
const HEADER_TYPE_DESIGNATION_FONT_SIZE = 16;
const HEADER_LINE_HEIGHT = 12;
const PRODUCT_CODE_MAX_VALUE_LINES = 2;

function isPngBytes(bytes) {
  return bytes.length >= 4
    && bytes[0] === 0x89
    && bytes[1] === 0x50
    && bytes[2] === 0x4e
    && bytes[3] === 0x47;
}

async function embedHeaderImage(pdfDoc, headerImageBytes) {
  if (isPngBytes(headerImageBytes)) {
    return pdfDoc.embedPng(headerImageBytes);
  }
  return pdfDoc.embedJpg(headerImageBytes);
}

/**
 * Normalize product field values (string or string[]) for the PDF header.
 * @param {*} value
 * @returns {string}
 */
export function formatPdfHeaderFieldValue(value) {
  if (value == null || value === '') {
    return '';
  }
  if (Array.isArray(value)) {
    return value
      .map((v) => (v == null ? '' : String(v).trim()))
      .filter((v) => v && v !== '\b')
      .join(', ');
  }
  const trimmed = String(value).trim();
  return trimmed === '\b' ? '' : trimmed;
}

/**
 * @param {string} value
 * @returns {string}
 */
function normalizeHeaderLine(value) {
  const trimmed = (value || '').toString().trim();
  return trimmed || '—';
}

/**
 * @param {Object} [headerInfo]
 * @returns {Object}
 */
function normalizeHeaderInfo(headerInfo = {
}) {
  return {
    issuanceDate: normalizeHeaderLine(headerInfo.issuanceDate),
    model: normalizeHeaderLine(headerInfo.model),
    productCode: normalizeHeaderLine(headerInfo.productCode),
    projectName: normalizeHeaderLine(headerInfo.projectName),
    projectNumber: normalizeHeaderLine(headerInfo.projectNumber),
    projectPhase: normalizeHeaderLine(headerInfo.projectPhase),
    typeDesignation: normalizeHeaderLine(headerInfo.typeDesignation),
    workspaceName: normalizeHeaderLine(headerInfo.workspaceName),
  };
}

/**
 * @param {string | undefined} keywords
 * @returns {string[]}
 */
function parsePdfKeywords(keywords) {
  if (!keywords || typeof keywords !== 'string') {
    return [];
  }
  return keywords.trim().split(/\s+/).filter(Boolean);
}

/**
 * @param {import('pdf-lib').PDFDocument} pdfDoc
 * @returns {boolean}
 */
export function pdfHasSourceryHeader(pdfDoc) {
  return parsePdfKeywords(pdfDoc.getKeywords()).includes(SOURCERY_HEADER_KEYWORD);
}

/**
 * @param {Uint8Array} pdfBytes
 * @returns {Promise<boolean>}
 */
export async function pdfBytesHaveSourceryHeader(pdfBytes) {
  const { PDFDocument } = await import('pdf-lib');
  const pdfDoc = await PDFDocument.load(pdfBytes);
  return pdfHasSourceryHeader(pdfDoc);
}

/** @param {import('pdf-lib').PDFDocument} pdfDoc */
function markSourceryHeader(pdfDoc) {
  const keywords = parsePdfKeywords(pdfDoc.getKeywords());
  if (!keywords.includes(SOURCERY_HEADER_KEYWORD)) {
    pdfDoc.setKeywords([...keywords, SOURCERY_HEADER_KEYWORD]);
  }
}

/**
 * @param {number} pageWidth
 * @param {{ width: number }} imgDims
 * @returns {{ col1: { x: number, width: number }, col2: { x: number, width: number }, col3: { x: number, width: number } }}
 */
function computeHeaderColumns(pageWidth, imgDims) {
  const contentLeft = HEADER_HORIZONTAL_MARGIN_PT;
  const contentRight = pageWidth - HEADER_HORIZONTAL_MARGIN_PT;
  const contentWidth = contentRight - contentLeft;

  const col1Width = imgDims.width;
  const col1 = {
    x: contentLeft,
    width: col1Width,
  };
  const col1Gap = col1Width > 0 ? HEADER_CELL_GAP_PT : 0;

  let col3Width = Math.min(HEADER_COL3_WIDTH_PT, Math.max(HEADER_COL3_MIN_WIDTH_PT, contentWidth * 0.40));
  const col3 = {
    x: contentRight - col3Width,
    width: col3Width,
  };

  let col2Width = col3.x - (col1.x + col1.width) - col1Gap - HEADER_CELL_GAP_PT;
  const minCol2Width = 120;
  if (col2Width < minCol2Width) {
    const deficit = minCol2Width - col2Width;
    col3Width = Math.max(HEADER_COL3_MIN_WIDTH_PT, col3Width - deficit);
    col3.x = contentRight - col3Width;
    col3.width = col3Width;
    col2Width = col3.x - (col1.x + col1.width) - col1Gap - HEADER_CELL_GAP_PT;
  }

  const col2 = {
    x: col1.x + col1.width + col1Gap,
    width: Math.max(0, col2Width),
  };

  return {
    col1, col2, col3,
  };
}

/**
 * @param {import('pdf-lib').PDFFont} font
 * @param {string} text
 * @param {number} fontSize
 * @returns {number}
 */
function measureTextWidth(font, text, fontSize) {
  return font.widthOfTextAtSize(text, fontSize);
}

/**
 * @param {string} token
 * @param {import('pdf-lib').PDFFont} font
 * @param {number} fontSize
 * @param {number} maxWidth
 * @returns {string[]}
 */
function breakLongToken(token, font, fontSize, maxWidth) {
  const lines = [];
  let start = 0;
  while (start < token.length) {
    let end = start + 1;
    while (end <= token.length
      && measureTextWidth(font, token.slice(start, end), fontSize) <= maxWidth) {
      end += 1;
    }
    if (end - start === 1) {
      lines.push(token[start]);
      start += 1;
    } else {
      lines.push(token.slice(start, end - 1));
      start = end - 1;
    }
  }
  return lines;
}

/**
 * Word-wrap with char-split fallback for tokens wider than maxWidth.
 *
 * @param {string} text
 * @param {import('pdf-lib').PDFFont} font
 * @param {number} fontSize
 * @param {number} maxWidth
 * @param {number} [maxLines]
 * @returns {{ lines: string[], remainder: string }}
 */
function wrapTextHybrid(text, font, fontSize, maxWidth, maxLines = Infinity) {
  if (!text) {
    return {
      lines: [''],
      remainder: '',
    };
  }
  if (maxWidth <= 0) {
    return {
      lines: [''],
      remainder: text,
    };
  }

  const lines = [];
  let index = 0;
  const len = text.length;

  while (index < len && lines.length < maxLines) {
    while (index < len && text[index] === ' ') {
      index += 1;
    }
    if (index >= len) {
      break;
    }

    let line = '';

    while (index < len) {
      let wordEnd = index;
      while (wordEnd < len && text[wordEnd] !== ' ') {
        wordEnd += 1;
      }
      const word = text.slice(index, wordEnd);
      const spacer = line ? ' ' : '';
      const candidate = line + spacer + word;

      if (measureTextWidth(font, candidate, fontSize) <= maxWidth) {
        line = candidate;
        index = wordEnd;
        while (index < len && text[index] === ' ') {
          index += 1;
        }
        continue;
      }

      if (line) {
        break;
      }

      const broken = breakLongToken(word, font, fontSize, maxWidth);
      line = broken[0];
      index += broken[0].length;
      break;
    }

    if (line) {
      lines.push(line);
    }
  }

  const remainder = index < len ? text.slice(index).trimStart() : '';

  return {
    lines: lines.length ? lines : [''],
    remainder,
  };
}

/**
 * @param {string} text
 * @param {import('pdf-lib').PDFFont} font
 * @param {number} fontSize
 * @param {number} maxWidth
 * @param {string} [ellipsis]
 * @returns {string}
 */
function truncateWithEllipsis(text, font, fontSize, maxWidth, ellipsis = HEADER_ELLIPSIS) {
  if (!text || measureTextWidth(font, text, fontSize) <= maxWidth) {
    return text;
  }
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const candidate = `${text.slice(0, mid)}${ellipsis}`;
    if (measureTextWidth(font, candidate, fontSize) <= maxWidth) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return `${text.slice(0, lo)}${ellipsis}`;
}

/**
 * Product code: line 1 beside label (narrow width), line 2 full column width.
 *
 * @param {string} value
 * @param {import('pdf-lib').PDFFont} font
 * @param {number} fontSize
 * @param {number} firstLineMaxWidth
 * @param {number} fullLineMaxWidth
 * @returns {string[]}
 */
function splitProductCodeValue(value, font, fontSize, firstLineMaxWidth, fullLineMaxWidth) {
  if (firstLineMaxWidth > 0 && measureTextWidth(font, value, fontSize) <= firstLineMaxWidth) {
    return [value];
  }

  const { lines: [line1], remainder } = wrapTextHybrid(
    value,
    font,
    fontSize,
    firstLineMaxWidth,
    1,
  );
  if (!remainder) {
    return [line1];
  }

  const { lines: fullLines } = wrapTextHybrid(remainder, font, fontSize, fullLineMaxWidth);
  if (fullLines.length <= 1) {
    return [line1, fullLines[0]];
  }

  return [
    line1,
    truncateWithEllipsis(remainder, font, fontSize, fullLineMaxWidth),
  ].slice(0, PRODUCT_CODE_MAX_VALUE_LINES);
}

/**
 * @param {import('pdf-lib').PDFPage} page
 * @param {Object} params
 */
function drawAlignedTextLine(page, {
  align = 'left',
  x,
  y,
  maxWidth,
  text,
  font,
  fontSize,
  rgb,
}) {
  if (!text) {
    return;
  }
  let lineX = x;
  if (align === 'right') {
    const lineWidth = measureTextWidth(font, text, fontSize);
    lineX = x + maxWidth - lineWidth;
  }
  page.drawText(text, {
    x: lineX,
    y,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  });
}

/**
 * @param {import('pdf-lib').PDFPage} page
 * @param {Object} params
 */
function drawFieldLabel(page, {
  fontBold,
  fontSize,
  labelText,
  maxWidth,
  rgb,
  x,
  y,
}) {
  if (!labelText) {
    return;
  }
  page.drawText(labelText, {
    x,
    y,
    size: fontSize,
    font: fontBold,
    color: rgb(0.2, 0.2, 0.2),
    maxWidth,
  });
}

/**
 * Col3 product code: label on row 1, wrapped value (max 2 lines, ellipsis on overflow).
 *
 * @param {import('pdf-lib').PDFPage} page
 * @param {Object} params
 * @returns {number} next text Y
 */
function drawLabeledWrapField(page, {
  x,
  y,
  maxWidth,
  label,
  value,
  fontRegular,
  fontBold,
  rgb,
  align,
  fontSize = HEADER_FONT_SIZE,
  lineHeight = HEADER_LINE_HEIGHT,
}) {
  const labelText = `${label}: `;
  const labelWidth = fontBold.widthOfTextAtSize(labelText, fontSize);
  const firstLineValueWidth = Math.max(0, maxWidth - labelWidth);
  const valueLines = splitProductCodeValue(
    value,
    fontRegular,
    fontSize,
    firstLineValueWidth,
    maxWidth,
  );

  let textY = y;

  drawFieldLabel(page, {
    fontBold,
    fontSize,
    labelText,
    maxWidth,
    rgb,
    x,
    y: textY,
  });
  drawAlignedTextLine(page, {
    align,
    x: x + labelWidth,
    y: textY,
    maxWidth: firstLineValueWidth,
    text: valueLines[0],
    font: fontRegular,
    fontSize,
    rgb,
  });
  textY -= lineHeight;

  if (valueLines[1]) {
    drawAlignedTextLine(page, {
      align,
      x,
      y: textY,
      maxWidth,
      text: valueLines[1],
      font: fontRegular,
      fontSize,
      rgb,
    });
    textY -= lineHeight;
  }

  return textY;
}

/**
 * @param {import('pdf-lib').PDFPage} page
 * @param {Object} params
 * @param {number} params.x
 * @param {number} params.y
 * @param {number} params.maxWidth
 * @param {{ label: string, value: string }[]} params.lines
 */
function drawLabeledLines(page, {
  x,
  y,
  maxWidth,
  lines,
  fontRegular,
  fontBold,
  rgb,
  fontSize = HEADER_FONT_SIZE,
}) {
  let textY = y;
  lines.forEach(({
    align = 'left',
    label,
    value,
    fontSize: lineFontSize,
    boldValue,
    skipLabel = false,
    wrap = false,
  }) => {
    const lineSize = lineFontSize ?? fontSize;

    if (wrap) {
      textY = drawLabeledWrapField(page, {
        x,
        y: textY,
        maxWidth,
        label,
        value,
        fontRegular,
        fontBold,
        rgb,
        align,
        fontSize: lineSize,
        lineHeight: HEADER_LINE_HEIGHT,
      });
      return;
    }
    const valueIsBold = boldValue ?? skipLabel;
    const labelText = skipLabel ? '' : `${label}: `;
    const labelWidth = labelText ? fontBold.widthOfTextAtSize(labelText, lineSize) : 0;
    const valueMaxWidth = Math.max(0, maxWidth - labelWidth);
    const valueFont = valueIsBold ? fontBold : fontRegular;

    if (labelText) {
      drawFieldLabel(page, {
        fontBold,
        fontSize: lineSize,
        labelText,
        maxWidth,
        rgb,
        x,
        y: textY,
      });
    }

    drawAlignedTextLine(page, {
      align,
      font: valueFont,
      fontSize: lineSize,
      maxWidth: valueMaxWidth,
      rgb,
      text: value,
      x: x + labelWidth,
      y: textY,
    });
    textY -= lineSize > HEADER_FONT_SIZE ? lineSize : HEADER_LINE_HEIGHT;
  });
}

/**
 * Header layout in PDF user space.
 *
 * Coordinate model (see AppPDFEditor/HEADER_ANNOTATIONS_PLAN.md):
 * - pdf-lib documents drawImage Y as bottom-left; EmbedPDF renders our header with Y *above*
 *   the page top instead. Logo Y, text baselines, and background all use that same convention.
 * - Existing page body is scaled down via scaleContent before we draw the header. Header draw
 *   calls still use pre-scale coordinates, so full-bleed width/height divide by contentScale.
 *
 * @param {Object} params
 * @param {number} params.pageWidth
 * @param {number} params.pageHeight
 * @param {number} params.contentScale - scaleContent factor applied to body (e.g. 0.92)
 * @param {{ width: number, height: number }} params.imgDims
 * @returns {{
 *   imgDims: { width: number, height: number },
 *   columns: ReturnType<typeof computeHeaderColumns>,
 *   logoY: number,
 *   textTopY: number,
 *   background: { x: number, y: number, width: number, height: number },
 * }}
 */
function buildHeaderLayout({
  contentScale,
  imgDims,
  pageHeight,
  pageWidth,
}) {
  const unscale = 1 / contentScale;
  const newPageHeight = pageHeight * unscale;
  const newPageWidth = pageWidth * unscale;

  // logo and text have top padding;
  const logoY = newPageHeight - HEADER_TOP_MARGIN_PT - imgDims.height;
  const textTopY = newPageHeight - HEADER_TOP_MARGIN_PT - HEADER_LINE_HEIGHT;

  return {
    imgDims,
    columns: computeHeaderColumns(newPageWidth, imgDims),
    logoY,
    textTopY,
    background: {
      x: 0,
      y: newPageHeight - PDF_HEADER_HEIGHT_PT,
      width: newPageWidth,
      height: PDF_HEADER_HEIGHT_PT,
    },
  };
}

/**
 * @param {Object} params
 * @param {number} params.pageWidth
 * @param {number} params.pageHeight
 * @param {number} params.contentScale
 * @param {{ width: number, height: number }} params.imgDims
 * @param {Map<string, ReturnType<typeof buildHeaderLayout>>} params.cache
 * @returns {ReturnType<typeof buildHeaderLayout>}
 */
function getCachedHeaderLayout({
  cache,
  contentScale,
  imgDims,
  pageHeight,
  pageWidth,
}) {
  const key = `${pageWidth}|${pageHeight}|${contentScale}|${imgDims.width}|${imgDims.height}`;
  const cached = cache.get(key);
  if (cached) {
    return cached;
  }

  const layout = buildHeaderLayout({
    contentScale,
    imgDims,
    pageHeight,
    pageWidth,
  });
  cache.set(key, layout);
  return layout;
}

/**
 * Three-column header: logo | project info | product type / designation.
 *
 * @param {import('pdf-lib').PDFPage} page
 * @param {Object} params
 */
function drawHeaderBand(page, {
  fontBold,
  fontRegular,
  headerImage,
  headerInfo,
  layout,
  rgb,
}) {
  const {
    imgDims,
    columns: { col1, col2, col3 },
    logoY,
    textTopY,
    background,
  } = layout;

  page.drawRectangle({
    ...background,
    color: rgb(1, 1, 1),
    borderWidth: 0,
  });

  if (headerImage) {
    page.drawImage(headerImage, {
      x: col1.x,
      y: logoY,
      width: imgDims.width,
      height: imgDims.height,
    });
  }

  drawLabeledLines(page, {
    x: col2.x,
    y: textTopY,
    maxWidth: col2.width,
    lines: [
      {
        label: 'Project',
        value: headerInfo.projectName,
      },
      {
        label: 'Project Number',
        value: headerInfo.projectNumber,
      },
      {
        label: 'Phase',
        value: headerInfo.projectPhase,
      },
      {
        label: 'Author',
        value: headerInfo.workspaceName,
      },
      {
        label: 'Date',
        value: headerInfo.issuanceDate,
      },
    ],
    fontRegular,
    fontBold,
    rgb,
  });

  drawLabeledLines(page, {
    x: col3.x,
    y: textTopY,
    maxWidth: col3.width,
    lines: [
      {
        label: 'Type / Designation',
        value: headerInfo.typeDesignation,
        align: 'right',
        fontSize: HEADER_TYPE_DESIGNATION_FONT_SIZE,
        boldValue: true,
        skipLabel: true,
      },
      {
        label: 'Product Code',
        value: headerInfo.productCode,
        wrap: true,
      },
      {
        label: 'Model',
        value: headerInfo.model,
      },
    ],
    fontRegular,
    fontBold,
    rgb,
  });
}

/**
 * Add or refresh the Sourcery header on every page.
 *
 * First apply (no `sourcery-header-v1` in PDF Keywords): scales existing page content and
 * annotations into the area below the 72pt band, then draws the header and sets Keywords.
 *
 * Re-apply (keyword already present): skips scaling, draws a fresh header band on top of the
 * existing one (white background covers prior header; fields refresh from headerInfo).
 *
 * Mutates the loaded document in place so page `/Annots` survive. Rebuilding pages
 * via embedPdf/drawPage would flatten content and drop viewer annotations.
 *
 * @param {Uint8Array} pdfBytes
 * @param {Uint8Array|null|undefined} headerImageBytes
 * @param {Object} [headerInfo]
 * @param {string} [headerInfo.projectName]
 * @param {string} [headerInfo.workspaceName]
 * @param {string} [headerInfo.issuanceDate]
 * @param {string} [headerInfo.typeDesignation]
 * @param {string} [headerInfo.productCode]
 * @param {string} [headerInfo.model]
 * @param {number} [headerHeightPt]
 * @returns {Promise<Uint8Array>}
 */
export async function addHeaderImageToAllPages(
  pdfBytes,
  headerImageBytes,
  headerInfo = {
  },
  headerHeightPt = PDF_HEADER_HEIGHT_PT,
) {
  const { PDFDocument, rgb, StandardFonts } = await import('pdf-lib');
  const pdfDoc = await PDFDocument.load(pdfBytes);
  const pages = pdfDoc.getPages();
  const pageCount = pages.length;
  if (pageCount === 0) {
    throw new Error('PDF has no pages');
  }

  const headerImage = headerImageBytes?.length
    ? await embedHeaderImage(pdfDoc, headerImageBytes)
    : null;
  const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const alreadyHeadered = pdfHasSourceryHeader(pdfDoc);
  const normalizedHeaderInfo = normalizeHeaderInfo(headerInfo);
  const layoutCache = new Map();
  const innerBandHeight = PDF_HEADER_HEIGHT_PT - (2 * HEADER_TOP_MARGIN_PT);
  const headerImgDims = headerImage
    ? headerImage.scaleToFit((innerBandHeight * 16) / 9, innerBandHeight)
    : {
      width: 0,
      height: 0,
    };

  pages.forEach((page, i) => {
    const { width, height } = page.getSize();
    if (height <= headerHeightPt) {
      throw new Error(`Page ${i + 1} is too small for the header band`);
    }
    let layoutScale = (height) / height;

    if (!alreadyHeadered) {
      layoutScale = (height - headerHeightPt) / height;
      // Shrink existing body into the area below the 72pt header band (first apply only).
      page.scaleContent(layoutScale, layoutScale);
      page.scaleAnnotations(layoutScale, layoutScale);
    }

    const layout = getCachedHeaderLayout({
      cache: layoutCache,
      contentScale: layoutScale,
      imgDims: headerImgDims,
      pageHeight: height,
      pageWidth: width,
    });

    // layoutScale positions the band consistently on first apply and re-apply.
    drawHeaderBand(page, {
      fontBold,
      fontRegular,
      headerImage,
      headerInfo: normalizedHeaderInfo,
      layout,
      rgb,
    });
  });

  markSourceryHeader(pdfDoc);

  return new Uint8Array(await pdfDoc.save());
}
